import React, { Fragment, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useStores } from '../../hooks/use-stores';
import ErrorMessage from '../../components/errormessage';
import { Block, palette, Heading2 } from '@awarego-ui/components';
import styled from 'styled-components';

const BlockTabs = styled.div`
    border-bottom: 1px solid ${palette.lightroom.heavyMetalLightHigh};
    margin-left: -32px;
    margin-right: -32px;
    margin-bottom: 16px;
    @media (max-width: 992px) {
        margin-left: -16px;
        margin-right: -16px;
    }

    ul {
        display: flex;
        padding-inline-start: 0px;
        margin-bottom: 0;
        margin-left: 56px;
        @media (max-width: 992px) {
            justify-content: center;
            margin-left: unset;
        }

        li {
            list-style: none;
            font-weight: 800;
            font-size: 20px;
            padding-bottom: 16px;
            margin-right: 40px;
            @media (max-width: 992px) {
                margin-right: 16px;
            }
            border-radius: 2px;
            transition: 0.2s border-bottom ease-out;

            a {
                color: inherit;
                text-decoration: none;
            }

            &.active,
            &:hover,
            &:focus-within {
                border-bottom: 3px solid ${(props) => props.theme.secondary};
            }
        }
    }
`;

function HomeViewContainer({
    layoutTitle,
    code,
    pageTitle,
    content,
    description,
}) {
    const { t } = useTranslation();
    const { store, companyStore } = useStores();
    const { currentCompany } = companyStore;
    const { error } = store;

    useEffect(() => {
        store.setLayoutTitle(t(layoutTitle));
    }, [store, t, layoutTitle]);

    const currentUserName = store.authStore.currentUser.name;

    const tabs = useMemo(
        () => [
            {
                code: 'trainings',
                title: 'Trainings',
                enabled: currentCompany && currentCompany.isTrainingEnabled,
                active: code === 'trainings',
            },
            {
                code: 'assessments',
                title: 'Assessments',
                enabled: currentCompany && currentCompany.isAssessmentEnabled,
                active: code === 'assessments',
            },
            {
                code: 'aware-expose',
                title: 'AwareExpose',
                enabled:
                    currentCompany &&
                    currentCompany.settings &&
                    currentCompany.settings['aware_expose.enabled'] === '1',
                active: code === 'aware-expose',
            },
        ],
        [currentCompany, code]
    );

    return (
        <Fragment>
            <Helmet>
                <title>{t(`Your ${pageTitle}`)}</title>
            </Helmet>
            <div className="container max-1280">
                <ErrorMessage error={error} />
                <div className="helmet">
                    <Heading2 as="h1">
                        {t('Welcome back')}, {currentUserName}
                    </Heading2>
                    <p>{description || `${t(`Here are your ${code}`)}.`}</p>
                </div>
                <Row>
                    <Col md="8" xs="12">
                        <Block>
                            <nav aria-label={t('Main')}>
                            <BlockTabs>
                                <ul>
                                    {tabs.map(
                                        (tab) =>
                                            tab.enabled && (
                                                <li
                                                    key={tab.code}
                                                    className={
                                                        tab.active
                                                            ? 'active'
                                                            : undefined
                                                    }
                                                >
                                                    <Link
                                                        to={`/${tab.code}`}
                                                        aria-current={
                                                            tab.active
                                                                ? 'page'
                                                                : undefined
                                                        }
                                                    >
                                                        {t(tab.title)}
                                                    </Link>
                                                </li>
                                            )
                                    )}
                                </ul>
                            </BlockTabs>
                            </nav>
                            {content}
                        </Block>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
}

export default observer(HomeViewContainer);
